import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cake, Sparkles, Users, Wallet, Ticket, AlertCircle, MessageCircle, Instagram, Bell, Heart, CalendarCheck } from "lucide-react";
import { SectionHeader, Stat } from "@/components/ui-bits";
import { getDashboard, listReminders, listPostCare } from "@/lib/api";
import { formatBRL, formatDate, waLink, igDMLink, composeBirthdayMessage, statusBadge, statusLabel } from "@/lib/format";
import { useMessageTemplates } from "@/lib/useTemplates";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [postCount, setPostCount] = useState(0);
  const templates = useMessageTemplates();

  useEffect(() => {
    getDashboard().then(setData).catch(() => setData({}));
    listReminders({ upcoming: true }).then((r) => setReminders(r.slice(0, 5))).catch(() => {});
    listPostCare(7).then((p) => setPostCount(p.length)).catch(() => {});
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <SectionHeader
        eyebrow="Visão Geral"
        title="Bem-vinda, Julliet"
        description="Um panorama elegante do seu estúdio — clientes, receitas e a sorte que você cria."
      />

      {!data ? (
        <p className="text-[#5C6658]">Carregando…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <Stat testId="stat-clients" label="Clientes" value={data.total_clients ?? 0} />
            <Stat testId="stat-tattoos-month" label="Tatuagens no Mês" value={data.tattoos_this_month ?? 0} />
            <Stat testId="stat-revenue" label="Receita do Mês" value={formatBRL(data.revenue_this_month)} accent />
            <Stat testId="stat-numbers" label="Números da Sorte" value={data.total_lucky_numbers ?? 0} />
          </div>

          {/* Today / Tomorrow scheduled tattoos */}
          <div className="elegant-card p-8 mt-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#E5EBE0] flex items-center justify-center">
                <CalendarCheck className="w-5 h-5 text-[#7B8B6F]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif-display text-2xl text-[#2E332C]">Agenda</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Hoje */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#7B8B6F] font-semibold">Hoje</h3>
                  <span className="text-xs text-[#5C6658]">{formatDate(data.today)}</span>
                </div>
                {(data.tattoos_today || []).length === 0 ? (
                  <p className="text-sm text-[#5C6658] italic py-4">Nenhuma sessão para hoje.</p>
                ) : (
                  <ul className="space-y-3" data-testid="tattoos-today">
                    {data.tattoos_today.map((t) => (
                      <ScheduleItem key={t.id} t={t} />
                    ))}
                  </ul>
                )}
              </div>

              {/* Amanhã */}
              <div className="md:border-l md:border-[#E8E4D9] md:pl-6">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#C4A77D] font-semibold">Amanhã</h3>
                  <span className="text-xs text-[#5C6658]">{formatDate(data.tomorrow)}</span>
                </div>
                {(data.tattoos_tomorrow || []).length === 0 ? (
                  <p className="text-sm text-[#5C6658] italic py-4">Nenhuma sessão para amanhã.</p>
                ) : (
                  <ul className="space-y-3" data-testid="tattoos-tomorrow">
                    {data.tattoos_tomorrow.map((t) => (
                      <ScheduleItem key={t.id} t={t} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            <div className="elegant-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#F4E8D6] flex items-center justify-center">
                  <Cake className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif-display text-2xl text-[#2E332C]">Aniversariantes do mês</h2>
              </div>
              {(data.birthdays_this_month || []).length === 0 ? (
                <p className="text-sm text-[#5C6658]">Nenhum aniversariante este mês.</p>
              ) : (
                <ul className="divide-y divide-[#E8E4D9]" data-testid="birthdays-list">
                  {data.birthdays_this_month.map((b) => (
                    <li key={b.id} className="flex items-center justify-between py-4 gap-3">
                      <div className="min-w-0 flex-1">
                        <Link to={`/clientes/${b.id}`} className="font-medium text-[#2E332C] hover:text-[#7B8B6F] block truncate">
                          {b.name}
                        </Link>
                        <p className="text-xs text-[#5C6658]">Dia {b.day}{b.instagram ? ` · @${b.instagram.replace(/^@/, "")}` : ""}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <BirthdayActions birthday={b} template={templates.birthday} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="elegant-card p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#FCEAEA] flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-[#B86B6B]" strokeWidth={1.5} />
                </div>
                <h2 className="font-serif-display text-2xl text-[#2E332C]">A receber</h2>
              </div>
              <p className="font-serif-display text-5xl text-[#2E332C]" data-testid="stat-pending">
                {formatBRL(data.pending_amount)}
              </p>
              <p className="text-sm text-[#5C6658] mt-2">
                Saldo restante (preço − sinal − pagos) de tatuagens pendentes/parciais.
              </p>

              <div className="grid grid-cols-2 gap-3 mt-8">
                <Link to="/clientes" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F1EEE3] hover:bg-[#E8E4D9] transition" data-testid="quick-clients">
                  <Users className="w-4 h-4 text-[#7B8B6F]" /><span className="text-sm">Clientes</span>
                </Link>
                <Link to="/tatuagens" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F1EEE3] hover:bg-[#E8E4D9] transition" data-testid="quick-tattoos">
                  <Sparkles className="w-4 h-4 text-[#7B8B6F]" /><span className="text-sm">Tatuagens</span>
                </Link>
                <Link to="/financeiro" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F1EEE3] hover:bg-[#E8E4D9] transition" data-testid="quick-financial">
                  <Wallet className="w-4 h-4 text-[#7B8B6F]" /><span className="text-sm">Financeiro</span>
                </Link>
                <Link to="/sorteio" className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#F1EEE3] hover:bg-[#E8E4D9] transition" data-testid="quick-draw">
                  <Ticket className="w-4 h-4 text-[#7B8B6F]" /><span className="text-sm">Sorteio</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="elegant-card p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E5EBE0] flex items-center justify-center">
                    <Bell className="w-5 h-5 text-[#7B8B6F]" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-serif-display text-2xl text-[#2E332C]">Próximos lembretes</h2>
                </div>
                <Link to="/lembretes" className="text-xs uppercase tracking-wider text-[#7B8B6F] hover:underline">Ver todos</Link>
              </div>
              {reminders.length === 0 ? (
                <p className="text-sm text-[#5C6658]">Nenhum lembrete agendado.</p>
              ) : (
                <ul className="divide-y divide-[#E8E4D9]" data-testid="upcoming-reminders">
                  {reminders.map((r) => (
                    <li key={r.id} className="py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-[#2E332C] font-medium truncate">{r.client_name}</p>
                          <p className="text-xs text-[#5C6658] capitalize">{r.type} · {formatDate(r.date)}</p>
                        </div>
                      </div>
                      {r.note && <p className="text-xs text-[#5C6658] italic mt-1">"{r.note}"</p>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="elegant-card p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F4E8D6] flex items-center justify-center">
                    <Heart className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-serif-display text-2xl text-[#2E332C]">Pós-Atendimento</h2>
                </div>
                <Link to="/pos-atendimento" className="text-xs uppercase tracking-wider text-[#7B8B6F] hover:underline">Abrir</Link>
              </div>
              <p className="font-serif-display text-5xl text-[#2E332C]" data-testid="stat-postcare">{postCount}</p>
              <p className="text-sm text-[#5C6658] mt-2">
                Clientes para receber mensagem de cicatrização (7+ dias).
              </p>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

function ScheduleItem({ t }) {
  return (
    <li className="border border-[#E8E4D9] rounded-xl p-3 bg-[#FDFCF7]">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-14 text-center">
          {t.time ? (
            <p className="font-serif-display text-xl text-[#7B8B6F] leading-none">{t.time}</p>
          ) : (
            <p className="text-xs uppercase tracking-wider text-[#5C6658]">s/ hora</p>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to={`/clientes/${t.client_id}`}
            className="font-medium text-[#2E332C] hover:text-[#7B8B6F] block truncate"
            data-testid={`schedule-client-${t.id}`}
          >
            {t.client_name}
          </Link>
          <p className="text-xs text-[#5C6658] mt-0.5 truncate">{t.description}</p>
          {t.body_part && <p className="text-[10px] uppercase tracking-wider text-[#5C6658] mt-1">{t.body_part}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif-display text-base text-[#2E332C]">{formatBRL(t.price)}</p>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusBadge(t.payment_status)}`}>
            {statusLabel(t.payment_status)}
          </span>
        </div>
      </div>
    </li>
  );
}

function BirthdayActions({ birthday, template }) {  const msg = composeBirthdayMessage(birthday.name.split(" ")[0], template);
  return (
    <>
      {birthday.instagram && (
        <a
          href={igDMLink(birthday.instagram)}
          target="_blank"
          rel="noreferrer"
          className="w-9 h-9 rounded-full bg-[#F1EEE3] hover:bg-[#E8E4D9] flex items-center justify-center"
          data-testid={`bday-ig-${birthday.id}`}
          title="Mandar mensagem no Instagram"
        >
          <Instagram className="w-4 h-4 text-[#7B5E2E]" />
        </a>
      )}
      <a
        href={waLink("", msg)}
        target="_blank"
        rel="noreferrer"
        className="w-9 h-9 rounded-full bg-[#E5EBE0] hover:bg-[#D6E0CB] flex items-center justify-center"
        data-testid={`bday-wa-${birthday.id}`}
        title="Mandar parabéns no WhatsApp"
      >
        <MessageCircle className="w-4 h-4 text-[#4F5E45]" />
      </a>
    </>
  );
}
